import { useState } from 'react';
import { Alert, Button, Checkbox, ConfigProvider, DatePicker, Form, Input, InputNumber, Select, Switch, Typography } from 'antd';
import { readableOn, type PublicForm, type PublicFormField } from '@crm/shared';
import { api, errorMessage, fileUrl } from '../api/client';

const isHex = (c: string | undefined): c is string => !!c && /^#[0-9a-fA-F]{6}$/.test(c);

/** The form's project logo, then its name as the page title (the title is left out of embeds). */
export function PublicFormHeader({ form, title = true }: { form: PublicForm; title?: boolean }) {
  const logo = form.project?.logoFileId;
  if (!logo && !title) return null;
  return (
    <>
      {logo && <img src={fileUrl(logo)} alt={form.project!.name} style={{ display: 'block', maxHeight: 56, maxWidth: 220, objectFit: 'contain', marginBottom: 20 }} />}
      {title && <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>{form.name}</Typography.Title>}
    </>
  );
}

/** A thin band in the form's main colour along the top of its card. */
export const brandBand = (form: PublicForm) => (isHex(form.theme.primaryColor) ? { borderTop: `4px solid ${form.theme.primaryColor}` } : {});

function Field({ f, ...rest }: { f: PublicFormField } & Record<string, unknown>) {
  const opts = (f.options ?? []).map((o) => ({ value: o.value, label: o.label }));
  switch (f.type) {
    case 'TEXTAREA': return <Input.TextArea placeholder={f.placeholder} autoSize={{ minRows: 3 }} {...rest} />;
    case 'NUMBER':
    case 'CURRENCY': return <InputNumber style={{ width: '100%' }} placeholder={f.placeholder} {...rest} />;
    case 'DATE': return <DatePicker style={{ width: '100%' }} {...rest} />;
    case 'DATETIME': return <DatePicker showTime style={{ width: '100%' }} {...rest} />;
    case 'BOOLEAN': return <Switch {...rest} />;
    case 'SELECT': return <Select allowClear placeholder={f.placeholder ?? 'Choose…'} options={opts} {...rest} />;
    case 'MULTISELECT': return <Select mode="multiple" allowClear placeholder={f.placeholder ?? 'Choose…'} options={opts} {...rest} />;
    case 'LIST': return <Select mode="tags" allowClear tokenSeparators={[',']} placeholder={f.placeholder ?? 'Type and press Enter'} {...rest} />;
    case 'EMAIL': return <Input type="email" placeholder={f.placeholder ?? 'you@company.com'} {...rest} />;
    case 'PHONE': return <Input type="tel" placeholder={f.placeholder} {...rest} />;
    case 'URL': return <Input placeholder={f.placeholder ?? 'https://'} {...rest} />;
    default: return <Input placeholder={f.placeholder} {...rest} />;
  }
}

/** Renders a public form from its schema. `preview` disables submission. */
export function PublicFormRenderer({ form, preview, onSubmitted }: { form: PublicForm; preview?: boolean; onSubmitted?: (r: { successMessage: string; redirectUrl: string | null }) => void }) {
  const [form$] = Form.useForm();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // the project's (or form's) colour on the button, focus rings, checkboxes and switches
  const primary = isHex(form.theme.primaryColor) ? form.theme.primaryColor : undefined;
  const onPrimary = primary ? readableOn(primary) : undefined;

  const submit = async (values: Record<string, unknown>) => {
    if (preview) return;
    setLoading(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {};
      for (const f of form.fields) {
        const v = values[f.key];
        payload[f.key] = v && typeof v === 'object' && 'toISOString' in (v as object) ? (v as { toISOString: () => string }).toISOString() : v;
      }
      payload._hp = values._hp ?? '';
      if (form.consentText) payload._consent = !!values._consent;
      const r = await api.post<{ successMessage: string; redirectUrl: string | null }>(`/f/${form.slug}`, payload);
      if (r.data.redirectUrl) {
        window.top?.location.assign(r.data.redirectUrl);
        return;
      }
      setDone(r.data.successMessage);
      onSubmitted?.(r.data);
    } catch (e) {
      setError(errorMessage(e, 'Could not submit the form'));
    } finally {
      setLoading(false);
    }
  };

  if (done) return <Alert type="success" showIcon title={done} />;

  return (
    <ConfigProvider theme={primary ? { token: { colorPrimary: primary, colorLink: primary, colorTextLightSolid: onPrimary } } : undefined}>
    <Form form={form$} layout="vertical" onFinish={submit} requiredMark="optional">
      {form.description && <Typography.Paragraph type="secondary">{form.description}</Typography.Paragraph>}
      {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 16 }}>
        {form.fields.map((f) => (
          <div key={f.key} style={{ gridColumn: f.width === 'half' ? undefined : '1 / -1' }}>
            <Form.Item
              name={f.key}
              label={f.label}
              extra={f.helpText}
              valuePropName={f.type === 'BOOLEAN' ? 'checked' : 'value'}
              rules={[...(f.required ? [{ required: true, message: `${f.label} is required` }] : []), ...(f.type === 'EMAIL' ? [{ type: 'email' as const, message: 'Enter a valid email' }] : [])]}
            >
              <Field f={f} />
            </Form.Item>
          </div>
        ))}
      </div>
      {/* honeypot: hidden from humans, filled by naive bots */}
      <Form.Item name="_hp" style={{ position: 'absolute', left: -10000, top: 'auto', width: 1, height: 1, overflow: 'hidden' }} aria-hidden>
        <Input tabIndex={-1} autoComplete="off" />
      </Form.Item>
      {form.consentText && (
        <Form.Item name="_consent" valuePropName="checked" rules={[{ validator: (_, v) => (v ? Promise.resolve() : Promise.reject(new Error('Tick this box to submit the form'))) }]}>
          <Checkbox>{form.consentText}</Checkbox>
        </Form.Item>
      )}
      <Button type="primary" htmlType="submit" loading={loading} disabled={preview} title={preview ? 'Preview only: submissions are disabled here' : undefined} block size="large" style={primary && preview ? { background: primary, borderColor: primary, color: onPrimary, opacity: 0.85 } : undefined}>
        {form.submitLabel}
      </Button>
    </Form>
    </ConfigProvider>
  );
}
